import React, { useEffect, useState, useRef } from 'react';
import { getFairPriceIndexCached } from '@/services/marketIntelService';
import PolymerLineChart, { LineDataset } from '@/components/charts/PolymerLineChart';
import { useAuth } from '@/contexts/AuthContext';
import { ACCESS_LEVELS } from '@rti/shared';
import { hasAccess } from '@/utils/commonUtils';
import { Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { CheckboxDropdown } from '@/components/common/CheckboxDropdown';
import { useTheme } from '@/contexts/ThemeContext';

const GRADES = [
  'HDPEBlowMLDG',
  'HDPEHMWFilm',
  'HDPEINJ',
  'LDPEGPFilm',
  'LLDPEButene',
  'LLDPEHexene',
] as const;

type Grade = typeof GRADES[number];

interface PEFairPriceTabProps {
  dateFrom?: string;
  dateTo?: string;
  labelMode?: 'overview' | 'tab';
  showBorder?: boolean;
  fullBleed?: boolean;
}

const PEFairPriceTab: React.FC<PEFairPriceTabProps> = ({ dateFrom, dateTo, labelMode = 'tab', fullBleed = false }) => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [allData, setAllData] = useState<any[]>([]);
  const [selectedGrade, setSelectedGrade] = useState<Grade>('HDPEBlowMLDG');
  const [selectedHdpe, setSelectedHdpe] = useState<Grade>('HDPEBlowMLDG');
  const [selectedLldpe, setSelectedLldpe] = useState<Grade>('LLDPEButene');
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);

  // Below 750px, hide chart data labels.
  const [hideDataLabels, setHideDataLabels] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches,
  );
  // Below 760px, show 1 grade button at a time with chevrons.
  const [isSmallGradeSelector, setIsSmallGradeSelector] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches,
  );
  const [isLargeGradeSelector, setIsLargeGradeSelector] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1399px)').matches,
  );
  const [isCenterExportButtonLarge, setIsCenterExportButtonLarge] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px) and (max-width: 1105px)').matches,
  );
  const [isSmallPolymerSelector, setIsSmallPolymerSelector] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 750px)').matches,
  );
  const [gradeSelectIndex, setGradeSelectIndex] = useState(0);
  const [polymerSelectIndex, setPolymerSelectIndex] = useState(0);

  const POLYMERS = ['HDPE', 'LDPE', 'LLDPE'] as const;

  useEffect(() => {
    const labelsMq = window.matchMedia('(max-width: 749px)');
    const gradesMq = window.matchMedia('(max-width: 749px)');
    const largeGradesMq = window.matchMedia('(max-width: 1399px)');
    const centerExportLargeMq = window.matchMedia('(min-width: 1024px) and (max-width: 1105px)');
    const smallPolymerMq = window.matchMedia('(max-width: 750px)');
    const labelsHandler = (e: MediaQueryListEvent) => setHideDataLabels(e.matches);
    const gradesHandler = (e: MediaQueryListEvent) => {
      setIsSmallGradeSelector(e.matches);
      setGradeSelectIndex(0);
    };
    const largeGradesHandler = (e: MediaQueryListEvent) => {
      setIsLargeGradeSelector(e.matches);
    };
    const centerExportLargeHandler = (e: MediaQueryListEvent) => {
      setIsCenterExportButtonLarge(e.matches);
    };
    const smallPolymerHandler = (e: MediaQueryListEvent) => {
      setIsSmallPolymerSelector(e.matches);
      setPolymerSelectIndex(0);
    };
    labelsMq.addEventListener('change', labelsHandler);
    gradesMq.addEventListener('change', gradesHandler);
    largeGradesMq.addEventListener('change', largeGradesHandler);
    centerExportLargeMq.addEventListener('change', centerExportLargeHandler);
    smallPolymerMq.addEventListener('change', smallPolymerHandler);
    return () => {
      labelsMq.removeEventListener('change', labelsHandler);
      gradesMq.removeEventListener('change', gradesHandler);
      largeGradesMq.removeEventListener('change', largeGradesHandler);
      centerExportLargeMq.removeEventListener('change', centerExportLargeHandler);
      smallPolymerMq.removeEventListener('change', smallPolymerHandler);
    };
  }, []);

  // Effective (company-resolved) tier governs access; fall back to raw/0.
  // Anything below Compound (i.e. Base or no company) is limited to 1 year of data.
  const userLevel = user?.effective_membership_id ?? 0;
  const restrictToOneYear = !hasAccess(userLevel, ACCESS_LEVELS.COMPOUND);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getFairPriceIndexCached(dateFrom, dateTo);
        let data = Array.isArray(result) ? result : [];

        // Base-tier (below Compound) users only see the last 1 year of data
        if (restrictToOneYear && data.length > 0) {
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          data = data.filter((d) => {
            const dateStr = String(d.Date || d.date || '').slice(0, 10);
            const itemDate = new Date(dateStr + 'T00:00:00');
            return itemDate >= oneYearAgo;
          });
        }

        setAllData(data);
      } catch (err) {
        console.error('PEFairPriceTab fetch error', err);
        setAllData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateFrom, dateTo, restrictToOneYear]);

  const handleExportCSV = () => {
    if (allData.length === 0) return;

    // Prepare data: parse dates and validate format
    let exportData = allData.map((d) => {
      const dateStr = String(d.Date || d.date || '').slice(0, 10);
      return { ...d, __dateStr: dateStr };
    }).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d.__dateStr));

    // Apply 1-year restriction for base-tier users
    if (restrictToOneYear && exportData.length > 0) {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      exportData = exportData.filter((d) => {
        const itemDate = new Date(d.__dateStr + 'T00:00:00');
        return itemDate >= oneYearAgo;
      });
    }

    // Sort by date (newest first)
    exportData.sort((a, b) => (a.__dateStr > b.__dateStr ? -1 : a.__dateStr < b.__dateStr ? 1 : 0));

    // Build CSV with all grades as columns
    const headers = ['Date', ...GRADES];
    const rows = exportData.map((d) => [
      d.__dateStr,
      ...GRADES.map((grade) => {
        const val = d[grade];
        return val !== null && val !== undefined ? val : '';
      }),
    ]);
    const csvContent = [headers, ...rows].map((r) => r.join(',')).join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `PE-Fair-Price-Full-Report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const filteredData = allData
    .map((d) => {
      const dateStr = String(d.Date || d.date || '').slice(0, 10);
      return { ...d, __dateStr: dateStr };
    })
    .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d.__dateStr))
    .sort((a, b) => (a.__dateStr < b.__dateStr ? -1 : a.__dateStr > b.__dateStr ? 1 : 0))
    .filter((d) => d[selectedGrade] !== null && d[selectedGrade] !== undefined);

  const labels = filteredData.map((d) => d.__dateStr);

  const values = filteredData.map((d) => {
    const v = d[selectedGrade];
    return v !== null && v !== undefined ? Number(v) : null;
  });

  const datasets: LineDataset[] = [
    {
      label: selectedGrade,
      data: values,
      color: 'rgba(132,204,22,0.9)',
    },
  ];

  return (
    <div className={`h-full flex flex-col ${fullBleed && isSmallGradeSelector ? '-mx-6' : 'w-full'}`}>
      {/* Grade selector + Export button — horizontal on big screens, stacked/centered below 760px */}
      <div className={`flex gap-3 mb-3 ${
        labelMode === 'overview'
          ? isSmallGradeSelector ? 'flex-col items-center' : isCenterExportButtonLarge ? 'flex-col items-center' : 'flex-row items-center justify-between'
          : (labelMode === 'tab' && isSmallPolymerSelector) ? 'flex-col items-center'
          : 'flex-row items-center justify-between'
      }`}>
        {labelMode === 'overview' ? (
          isLargeGradeSelector ? (
            // Below 1400px: 1 grade option at a time with chevrons
            <div className="flex items-center gap-0">
              <button
                onClick={() => {
                  const newIndex = Math.max(0, gradeSelectIndex - 1);
                  setGradeSelectIndex(newIndex);
                }}
                disabled={gradeSelectIndex === 0}
                className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green' : 'text-black'} hover:bg-rs-jet disabled:opacity-40`}
                aria-label="Previous grade type"
              >
                <ChevronLeft size={28} strokeWidth={2.5} />
              </button>

              {gradeSelectIndex === 0 && (
                <CheckboxDropdown
                  label="HDPE"
                  options={['HDPEBlowMLDG', 'HDPEHMWFilm', 'HDPEINJ']}
                  selected={new Set([selectedGrade.startsWith('HDPE') ? selectedGrade : selectedHdpe])}
                  onChange={(selected) => {
                    const grade = Array.from(selected)[0] as Grade;
                    setSelectedHdpe(grade);
                    setSelectedGrade(grade);
                  }}
                  singleSelect={true}
                  hideCount={true}
                  isActive={selectedGrade.startsWith('HDPE')}
                  shiftMenuRight={true}
                />
              )}

              {gradeSelectIndex === 1 && (
                <button
                  onClick={() => setSelectedGrade('LDPEGPFilm')}
                  className={`px-3 py-2 rounded text-sm font-medium transition-colors border ${
                    selectedGrade === 'LDPEGPFilm'
                      ? 'bg-rs-green text-black border-rs-green'
                      : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light border-rs-jet-light'
                  }`}
                >
                  LDPE GP Film
                </button>
              )}

              {gradeSelectIndex === 2 && (
                <CheckboxDropdown
                  label="LLDPE"
                  options={['LLDPEButene', 'LLDPEHexene']}
                  selected={new Set([selectedGrade.startsWith('LLDPE') ? selectedGrade : selectedLldpe])}
                  onChange={(selected) => {
                    const grade = Array.from(selected)[0] as Grade;
                    setSelectedLldpe(grade);
                    setSelectedGrade(grade);
                  }}
                  singleSelect={true}
                  hideCount={true}
                  isActive={selectedGrade.startsWith('LLDPE')}
                />
              )}

              <button
                onClick={() => {
                  const newIndex = Math.min(2, gradeSelectIndex + 1);
                  setGradeSelectIndex(newIndex);
                }}
                disabled={gradeSelectIndex >= 2}
                className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green' : 'text-black'} hover:bg-rs-jet disabled:opacity-40`}
                aria-label="Next grade type"
              >
                <ChevronRight size={28} strokeWidth={2.5} />
              </button>
            </div>
          ) : (
            // 1400px+: original layout with all dropdowns
            <div className="flex flex-wrap gap-1.5">
              <CheckboxDropdown
                label="HDPE"
                options={['HDPEBlowMLDG', 'HDPEHMWFilm', 'HDPEINJ']}
                selected={new Set([selectedGrade.startsWith('HDPE') ? selectedGrade : selectedHdpe])}
                onChange={(selected) => {
                  const grade = Array.from(selected)[0] as Grade;
                  setSelectedHdpe(grade);
                  setSelectedGrade(grade);
                }}
                singleSelect={true}
                hideCount={true}
                isActive={selectedGrade.startsWith('HDPE')}
                shiftMenuRight={true}
              />

              <button
                onClick={() => setSelectedGrade('LDPEGPFilm')}
                className={`px-3 py-2 rounded text-sm font-medium transition-colors border ${
                  selectedGrade === 'LDPEGPFilm'
                    ? 'bg-rs-green text-black border-rs-green'
                    : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light border-rs-jet-light'
                }`}
              >
                LDPE GP Film
              </button>

              <CheckboxDropdown
                label="LLDPE"
                options={['LLDPEButene', 'LLDPEHexene']}
                selected={new Set([selectedGrade.startsWith('LLDPE') ? selectedGrade : selectedLldpe])}
                onChange={(selected) => {
                  const grade = Array.from(selected)[0] as Grade;
                  setSelectedLldpe(grade);
                  setSelectedGrade(grade);
                }}
                singleSelect={true}
                hideCount={true}
                isActive={selectedGrade.startsWith('LLDPE')}
              />
            </div>
          )
        ) : labelMode === 'tab' && isSmallPolymerSelector ? (
          // Tab mode below 750px: 1 polymer at a time with chevrons
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const newIndex = Math.max(0, polymerSelectIndex - 1);
                setPolymerSelectIndex(newIndex);
              }}
              disabled={polymerSelectIndex === 0}
              className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green' : 'text-black'} hover:bg-rs-jet disabled:opacity-40`}
              aria-label="Previous polymer"
            >
              <ChevronLeft size={28} strokeWidth={2.5} />
            </button>

            {polymerSelectIndex === 0 && (
              <CheckboxDropdown
                label="HDPE"
                options={['HDPEBlowMLDG', 'HDPEHMWFilm', 'HDPEINJ']}
                selected={new Set([selectedGrade.startsWith('HDPE') ? selectedGrade : selectedHdpe])}
                onChange={(selected) => {
                  const grade = Array.from(selected)[0] as Grade;
                  setSelectedHdpe(grade);
                  setSelectedGrade(grade);
                }}
                singleSelect={true}
                hideCount={true}
                isActive={selectedGrade.startsWith('HDPE')}
                shiftMenuRight={true}
              />
            )}

            {polymerSelectIndex === 1 && (
              <button
                onClick={() => setSelectedGrade('LDPEGPFilm')}
                className={`px-3 py-2 rounded text-sm font-medium transition-colors border ${
                  selectedGrade === 'LDPEGPFilm'
                    ? 'bg-rs-green text-black border-rs-green'
                    : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light border-rs-jet-light'
                }`}
              >
                LDPE GP Film
              </button>
            )}

            {polymerSelectIndex === 2 && (
              <CheckboxDropdown
                label="LLDPE"
                options={['LLDPEButene', 'LLDPEHexene']}
                selected={new Set([selectedGrade.startsWith('LLDPE') ? selectedGrade : selectedLldpe])}
                onChange={(selected) => {
                  const grade = Array.from(selected)[0] as Grade;
                  setSelectedLldpe(grade);
                  setSelectedGrade(grade);
                }}
                singleSelect={true}
                hideCount={true}
                isActive={selectedGrade.startsWith('LLDPE')}
              />
            )}

            <button
              onClick={() => {
                const newIndex = Math.min(2, polymerSelectIndex + 1);
                setPolymerSelectIndex(newIndex);
              }}
              disabled={polymerSelectIndex >= 2}
              className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green' : 'text-black'} hover:bg-rs-jet disabled:opacity-40`}
              aria-label="Next polymer"
            >
              <ChevronRight size={28} strokeWidth={2.5} />
            </button>
          </div>
        ) : (
          // Tab mode 750px+: show original layout with all dropdowns
          <div className="flex flex-wrap gap-1.5">
            <CheckboxDropdown
              label="HDPE"
              options={['HDPEBlowMLDG', 'HDPEHMWFilm', 'HDPEINJ']}
              selected={new Set([selectedGrade.startsWith('HDPE') ? selectedGrade : selectedHdpe])}
              onChange={(selected) => {
                const grade = Array.from(selected)[0] as Grade;
                setSelectedHdpe(grade);
                setSelectedGrade(grade);
              }}
              singleSelect={true}
              hideCount={true}
              isActive={selectedGrade.startsWith('HDPE')}
              shiftMenuRight={true}
            />

            <button
              onClick={() => setSelectedGrade('LDPEGPFilm')}
              className={`px-3 py-2 rounded text-sm font-medium transition-colors border ${
                selectedGrade === 'LDPEGPFilm'
                  ? 'bg-rs-green text-black border-rs-green'
                  : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light border-rs-jet-light'
              }`}
            >
              LDPE GP Film
            </button>

            <CheckboxDropdown
              label="LLDPE"
              options={['LLDPEButene', 'LLDPEHexene']}
              selected={new Set([selectedGrade.startsWith('LLDPE') ? selectedGrade : selectedLldpe])}
              onChange={(selected) => {
                const grade = Array.from(selected)[0] as Grade;
                setSelectedLldpe(grade);
                setSelectedGrade(grade);
              }}
              singleSelect={true}
              hideCount={true}
              isActive={selectedGrade.startsWith('LLDPE')}
            />
          </div>
        )}

        <button
          onClick={handleExportCSV}
          disabled={filteredData.length === 0 || loading}
          className={`flex items-center gap-2 px-3 py-2 rounded text-sm font-medium bg-rs-green text-black hover:bg-rs-dark-green disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-fit`}
          title={restrictToOneYear ? 'Export limited to last 1 year (Base tier)' : 'Export all data'}
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>

      <div className={`border border-rs-jet-light rounded-lg ${fullBleed && isSmallGradeSelector ? 'p-2' : 'p-4'} ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
        <h3 className={`text-rs-off-white font-medium text-sm mb-1 ${isSmallGradeSelector ? 'text-center' : ''}`}>
          Fair Resin Price — {selectedGrade}
        </h3>
        <p className={`text-rs-off-white/50 text-sm mb-3 ${isSmallGradeSelector ? 'text-center' : ''}`}>$/lb</p>
        {loading ? (
          <div className="flex items-center justify-center text-rs-off-white/50 h-[400px]">
            Loading fair price data…
          </div>
        ) : (
          <div className="h-[400px]" data-testid="fair-price-chart">
            <PolymerLineChart
              className={`w-full h-full rounded ${isDark ? 'bg-rs-jet' : 'bg-transparent'}`}
              datasets={datasets}
              labels={labels}
              yLabel="$/lb"
              showDataLabels={labelMode !== 'overview' && !hideDataLabels}
              dataLabelMaxWindowDays={labelMode === 'overview' ? 730 : 365}
              alwaysShowDataLabels={labelMode === 'tab'}
              dataLabelDecimals={2}
              rightPadding={fullBleed && isSmallGradeSelector ? 0 : 20}
              showRangeSlider
              threeYearDateLabels
            />
          </div>
        )}
      </div>
      <p data-source-tag className={`text-rs-highlight-gray text-xs mt-3 ${fullBleed && isSmallGradeSelector ? 'px-4' : ''}`}>Source: RTI</p>
    </div>
  );
};

export default PEFairPriceTab;
